import { useRef } from 'react'
import { Upload, FileSpreadsheet } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { parseCsv, tableFromMatrix, type ParsedTable } from '../../lib/csv'

export interface UploadedFile {
  name: string
  format: 'csv' | 'xlsx'
  parsed: ParsedTable
}

async function parseFile(file: File): Promise<UploadedFile> {
  const isXlsx = /\.xlsx?$/i.test(file.name) || file.type.includes('sheet')
  if (isXlsx) {
    const XLSX = await import('xlsx')
    const buf = await file.arrayBuffer()
    const wb = XLSX.read(buf, { type: 'array' })
    const ws = wb.Sheets[wb.SheetNames[0]]
    const matrix = XLSX.utils.sheet_to_json<string[]>(ws, { header: 1, raw: false, defval: '' })
    return { name: file.name, format: 'xlsx', parsed: tableFromMatrix(matrix as string[][]) }
  }
  const text = await file.text()
  return { name: file.name, format: 'csv', parsed: parseCsv(text) }
}

export function StepUpload({
  file,
  onParsed,
}: {
  file: UploadedFile | null
  onParsed: (f: UploadedFile) => void
}) {
  const { toast } = useToast()
  const ref = useRef<HTMLInputElement>(null)

  const onSelect = async (f: File) => {
    try {
      const parsed = await parseFile(f)
      if (parsed.parsed.headers.length === 0) {
        toast({ variant: 'destructive', title: 'فایل خالی', description: 'سرستونی یافت نشد.' })
        return
      }
      onParsed(parsed)
    } catch (e) {
      toast({ variant: 'destructive', title: 'خطا در خواندن فایل', description: (e as Error).message })
    }
  }

  const preview = file?.parsed
  const previewRows = preview?.rows.slice(0, 8) ?? []

  return (
    <div className="space-y-4">
      <input
        ref={ref}
        type="file"
        accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) onSelect(f)
        }}
      />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        className="w-full rounded-xl border-2 border-dashed border-border hover:border-primary/50 hover:bg-muted/20 transition-colors p-8 flex flex-col items-center gap-2"
      >
        <Upload className="w-7 h-7 text-muted-foreground" />
        <span className="text-sm font-medium">{file ? 'انتخاب فایل دیگر' : 'انتخاب فایل CSV یا Excel'}</span>
        <span className="text-xs text-muted-foreground">سطر اول باید سرستون‌ها باشد</span>
      </button>

      {file && preview && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <FileSpreadsheet className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium">{file.name}</span>
            <span className="text-xs text-muted-foreground">
              {preview.headers.length} ستون · {preview.rows.length} ردیف
            </span>
          </div>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-xs">
              <thead className="bg-muted/40">
                <tr>
                  {preview.headers.map((h) => (
                    <th key={h} className="px-2 py-1.5 text-start font-medium whitespace-nowrap font-mono">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((r, i) => (
                  <tr key={i} className="border-t border-border">
                    {preview.headers.map((h) => (
                      <td key={h} className="px-2 py-1 whitespace-nowrap max-w-[200px] truncate">
                        {r[h]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.rows.length > 8 && (
            <p className="text-xs text-muted-foreground">۸ ردیف اول نمایش داده شده.</p>
          )}
        </div>
      )}
    </div>
  )
}
